import * as React from "react"
import { createStorefrontApiClient, StorefrontApiClient } from "@shopify/storefront-api-client"
import { Cart } from "../types/storefront.types"
import { CartDetailsFragment } from "../types/storefront.generated"


const client = createStorefrontApiClient({
  storeDomain: process.env.GATSBY_SHOPIFY_STORE_URL!,
  apiVersion: "2025-01",
  publicAccessToken: process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
})

const defaultValues: {
  isOpen: boolean
  loading: boolean
  onOpen: () => void
  onClose: () => void
  addVariantToCart: (variantId: string, quantity: string | number) => void
  updateLineItem: (cartId: string, lineItemID: string, quantity: number) => void
  removeLineItem: (cartId: string, lineItemID: string) => void
  client: StorefrontApiClient
  cart: CartDetailsFragment | undefined
  didJustAddToCart: boolean
} = {
  isOpen: false,
  loading: false,
  onOpen: () => {},
  onClose: () => {},
  addVariantToCart: () => {},
  updateLineItem: () => {},
  removeLineItem: () => {},
  client,
  cart: undefined,
  didJustAddToCart: false,
}

export const StoreContext = React.createContext(defaultValues)

const isBrowser = typeof window !== `undefined`
const localStorageKey = `shopify_cart_id`

const CartDetails = `#graphql
fragment CartDetails on Cart {
  id
  checkoutUrl
  estimatedCost {
    subtotalAmount {
      amount
      currencyCode
    }
    totalAmount {
      amount
      currencyCode
    }
  }
  lines (first: 50) {
    nodes {
      ...CartLineDetails
    }
  }
}

fragment CartLineDetails on CartLine {
  id
  quantity
  merchandise {
    ... on ProductVariant {
      id
      title
      image {
        altText
        url
        height
        width
      }
      price {
        amount
        currencyCode
      }
      product {
        title
        handle
      }
    }
  }
}
`;

export const StoreProvider = ({ children }: {children: React.ReactNode}) => {
  const [cart, setCart] = React.useState<CartDetailsFragment | undefined>(undefined)
  const [loading, setLoading] = React.useState(false)
  const [didJustAddToCart, setDidJustAddToCart] = React.useState(false)

  const setCartItem = (cart: CartDetailsFragment) => {
    if (isBrowser) {
      localStorage.setItem(localStorageKey, cart.id!)
    }

    setCart(cart)
  }

  React.useEffect(() => {
    const initializeCart = async () => {
      const existingCartID = isBrowser
        ? localStorage.getItem(localStorageKey)
        : null


      if (existingCartID && existingCartID !== `null`) {
        try {
          const operation = `#graphql
            query ExistingCart($id: ID!) {
              cart (id: $id) {
                ...CartDetails
              }
            }
            
            ${CartDetails}
          `;
          const existingCart = await client.request(operation, {variables: {id: existingCartID}});
          if (existingCart.data && existingCart.data.cart) {
            setCartItem(existingCart.data.cart)
            return
          }
        } catch (e) {
          localStorage.setItem(localStorageKey, "")
        }
      }

      const operation = `#graphql
        mutation CreateCart {
          cartCreate (
            input: {
            }
          ) {
            cart {
              ...CartDetails
            }
          }
        }
        
        ${CartDetails}
      `;

      const newCart = await client.request(operation);
      if (newCart.data && newCart.data.cartCreate && newCart.data.cartCreate.cart) {
        setCartItem(newCart.data.cartCreate.cart);
      }
      else {
        console.error("Could not create new cart!");
      }
    }

    initializeCart()
  }, [])

  const addVariantToCart = (variantId: string, quantity: string | number) => {
    if (!cart) {
      console.error("Cart not loaded");
      return;
    }
    setLoading(true)

    const cartId = cart.id

    const operation = `#graphql
        mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
          cartLinesAdd(cartId: $cartId, lines: $lines) {
            cart {
              ...CartDetails
            }
          }
        }
        
        ${CartDetails}
      `;
    const variables = {
      cartId: cartId,
      lines: [
        {
          merchandiseId: variantId,
          quantity: typeof quantity === "string" ? parseInt(quantity, 10) : quantity,
        }
      ]
    }

    client.request(operation, {variables: variables}).then((res) => {
      if (res.data && res.data.cartLinesAdd && res.data.cartLinesAdd.cart) {
        setCart(res.data.cartLinesAdd.cart);
      }
      setLoading(false);
      setDidJustAddToCart(true);
      setTimeout(() => setDidJustAddToCart(false), 3000)
    });
  }

  const removeLineItem = (cartId: string, lineItemID: string) => {
    setLoading(true);

    const operation = `#graphql
        mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
          cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
            cart {
              ...CartDetails
            }
          }
        }
        
        ${CartDetails}
      `;
    const variables = {
      cartId: cartId,
      lineIds: [lineItemID],
    }

    client.request(operation, {variables: variables}).then((res) => {
      if (res.data && res.data.cartLinesRemove && res.data.cartLinesRemove.cart) {
        setCart(res.data.cartLinesRemove.cart);
      }
      setLoading(false);
    });
  }

  const updateLineItem = (cartId: string, lineItemID: string, quantity: number) => {
    setLoading(true)

    const operation = `#graphql
        mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
          cartLinesUpdate(cartId: $cartId, lines: $lines) {
            cart {
              ...CartDetails
            }
          }
        }
        
        ${CartDetails}
      `;
    const variables = {
      cartId: cartId,
      lines: [
        {
          id: lineItemID,
          quantity: quantity,
        }
      ]
    }

    client.request(operation, {variables: variables}).then((res) => {
      if (res.data && res.data.cartLinesUpdate && res.data.cartLinesUpdate.cart) {
        setCart(res.data.cartLinesUpdate.cart);
      }
      setLoading(false);
    });
  }

  return (
    <StoreContext.Provider
      value={{
        ...defaultValues,
        addVariantToCart,
        removeLineItem,
        updateLineItem,
        cart,
        loading,
        didJustAddToCart,
      }}
    >
      {children}
    </StoreContext.Provider>
  )
}
